import React, { useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import "./mantenedora.css";
import Button from "../../../components/button/button";
import Badge from "../../../components/badge/badge";
import PlanChangeModal, {
  isOnRequestPlan,
  planDisplayName
} from "../../../components/planChangeModal/planChangeModal";
import HeaderTitle from "../../../components/header/headerTitle/headerTitle";
import ErrorMessage from "../../../components/forms/errorMessage/errorMessage";
import CheckedCircle from "../../../assets/icons/checked-circle.svg";
import { useRole } from "../../../context/RoleContext";
import { useMantenedora } from "../../../context/MantenedoraContext";
import { getMantenedoraWithPlans, updateMantenedora } from "../../../services/mantenedora";

const PLAN_CHANGE_ROLES = ["Admin", "MantenedoraAdmin"];

const formatBRL = (value) =>
  value === null || value === undefined
    ? "—"
    : value.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
      });

const formatOnboarding = (hours) =>
  hours === null || hours === undefined ? "Sob consulta" : `${hours} hrs`;

// Plano sob consulta (topo da tabela) usa "A partir de"; os demais, "Até".
const formatPlanStudents = (plan) =>
  `${isOnRequestPlan(plan) ? "A partir de" : "Até"} ${plan.maxActiveStudents.toLocaleString("pt-BR")}`;

const formatNumber = (value) =>
  typeof value === "number" && !Number.isNaN(value)
    ? value.toLocaleString("pt-BR")
    : "—";

const Mantenedora = () => {
  const { role } = useRole();
  const { mantenedora, setMantenedora } = useMantenedora();

  const canChangePlan = PLAN_CHANGE_ROLES.includes(role);

  const [loading, setLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [data, setData] = useState(null);

  // Fluxo de troca de plano: confirmação -> salvar -> modal de sucesso.
  const [pendingForm, setPendingForm] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm();

  const selectedPlanId = watch("planId");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoadingData(true);
        const result = await getMantenedoraWithPlans();
        if (result) {
          setData(result);
          reset({
            name: result.name,
            planId: result.planId || ""
          });
        } else {
          toast.error("Erro ao carregar dados da mantenedora");
        }
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [reset]);

  const plans = useMemo(() => data?.plans || [], [data]);

  const selectedPlan = useMemo(
    () => plans.find((p) => p.id === selectedPlanId) || null,
    [plans, selectedPlanId]
  );

  // Uso é sempre medido contra o plano CONTRATADO (data.planId), não o
  // selecionado no dropdown (que ainda não foi salvo).
  const contractedPlan = useMemo(
    () => plans.find((p) => p.id === data?.planId) || null,
    [plans, data]
  );

  const activeStudents = data?.activeStudents ?? 0;
  const maxStudents = contractedPlan?.maxActiveStudents ?? 0;
  const usagePct =
    maxStudents > 0
      ? Math.min(100, Math.round((activeStudents / maxStudents) * 100))
      : 0;
  const limitExceeded = maxStudents > 0 && activeStudents > maxStudents;

  // Plano "cabe" se comporta o uso atual. O plano sob consulta ("a partir de")
  // sempre cabe; o contratado também, para não quebrar o valor atual do select.
  const planFits = (plan) =>
    isOnRequestPlan(plan) ||
    plan.id === data?.planId ||
    plan.maxActiveStudents >= activeStudents;

  const planIsChanging = (formData) =>
    canChangePlan && (formData.planId || "") !== (data?.planId || "");

  // Decide o caminho: troca de plano passa pelo modal de confirmação;
  // alteração só do nome salva direto.
  const onSubmit = (formData) => {
    if (!data?.id) return;

    if (planIsChanging(formData)) {
      setPendingForm(formData);
      setShowConfirmModal(true);
      return;
    }

    performSave(formData, false);
  };

  const performSave = async (formData, isPlanChange) => {
    if (!data?.id) return;

    const payload = { name: formData.name };
    if (canChangePlan) {
      payload.planId = formData.planId || null;
    }

    setLoading(true);
    try {
      await updateMantenedora(data.id, payload);

      setData((prev) => ({
        ...prev,
        name: formData.name,
        planId: payload.planId ?? prev.planId
      }));

      // Mantém o nome exibido na sidebar/contexto em sincronia.
      if (mantenedora) {
        setMantenedora({ ...mantenedora, name: formData.name });
      }

      if (isPlanChange) {
        setShowConfirmModal(false);
        setShowSuccessModal(true);
      } else {
        toast.success("Mantenedora atualizada com sucesso!");
      }
    } catch (error) {
      toast.error(error.message || "Erro ao atualizar mantenedora");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPlanChange = () => {
    if (pendingForm) {
      performSave(pendingForm, true);
    }
  };

  const handleCloseConfirm = () => {
    if (loading) return;
    setShowConfirmModal(false);
    setPendingForm(null);
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
    setPendingForm(null);
  };

  // Plano de origem (contratado) e destino (selecionado) para os modais.
  const fromPlan = contractedPlan;
  const toPlan = pendingForm
    ? plans.find((p) => p.id === pendingForm.planId) || null
    : selectedPlan;

  if (isLoadingData) {
    return (
      <div>
        <HeaderTitle title="Mantenedora" />
        <div className="mainContentSpace">
          <div className="mantenedora-loading">
            <span>Carregando dados da mantenedora...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <HeaderTitle title="Mantenedora" />

      <div className="mainContentSpace">
        <div className="mantenedora-container">
          <form onSubmit={handleSubmit(onSubmit)} className="mantenedora-form">
            <fieldset>
              <legend>Dados da Mantenedora</legend>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">Nome da Mantenedora</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Nome da mantenedora"
                    {...register("name", {
                      required: "O nome é obrigatório.",
                      minLength: {
                        value: 2,
                        message: "O nome deve ter no mínimo 2 caracteres."
                      },
                      maxLength: {
                        value: 100,
                        message: "O nome deve ter no máximo 100 caracteres."
                      }
                    })}
                  />
                  <ErrorMessage message={errors.name?.message} />
                </div>

                <div className="form-group">
                  <label htmlFor="planId">Plano contratado</label>
                  <select
                    id="planId"
                    name="planId"
                    disabled={!canChangePlan}
                    {...register("planId", {
                      required: "Selecione um plano."
                    })}
                  >
                    <option value="">Selecione um plano</option>
                    {plans.map((plan) => {
                      const fits = planFits(plan);
                      return (
                        <option
                          key={plan.id}
                          value={plan.id}
                          disabled={!fits}
                          title={
                            fits
                              ? undefined
                              : `Sua mantenedora possui ${formatNumber(
                                  activeStudents
                                )} aluno(s) ativo(s), mais do que este plano suporta (${formatNumber(
                                  plan.maxActiveStudents
                                )}).`
                          }
                        >
                          {planDisplayName(plan)} ({formatPlanStudents(plan)}{" "}
                          alunos)
                        </option>
                      );
                    })}
                  </select>
                  <ErrorMessage message={errors.planId?.message} />
                  {!canChangePlan && (
                    <span className="mantenedora-hint">
                      Apenas o administrador da rede pode alterar o plano contratado.
                    </span>
                  )}
                </div>
              </div>

              {isOnRequestPlan(selectedPlan) && (
                <div className="mantenedora-on-request">
                  <strong>Plano sob consulta.</strong> Ao salvar, nossa equipe
                  entrará em contato para finalizar a contratação deste plano.
                </div>
              )}
            </fieldset>

            <fieldset>
              <legend>Uso do plano contratado</legend>

              <div className="mantenedora-usage">
                <div className="mantenedora-usage-header">
                  <span>
                    <strong>{formatNumber(activeStudents)}</strong> aluno(s)
                    ativo(s) importado(s)
                  </span>
                  <span>
                    Limite do plano
                    {contractedPlan ? ` ${planDisplayName(contractedPlan)}` : ""}:{" "}
                    <strong>{formatNumber(maxStudents)}</strong>
                  </span>
                </div>

                <div
                  className="mantenedora-progress"
                  role="progressbar"
                  aria-valuenow={activeStudents}
                  aria-valuemin={0}
                  aria-valuemax={maxStudents || undefined}
                >
                  <div
                    className={
                      "mantenedora-progress-fill" +
                      (limitExceeded ? " is-exceeded" : "")
                    }
                    style={{ width: `${usagePct}%` }}
                  />
                </div>

                <div className="mantenedora-usage-footer">
                  {limitExceeded ? (
                    <span className="mantenedora-usage-alert">
                      Limite excedido em{" "}
                      <strong>
                        {formatNumber(activeStudents - maxStudents)}
                      </strong>{" "}
                      aluno(s) ativo(s). Considere migrar para um plano maior.
                    </span>
                  ) : (
                    <span>
                      {usagePct}% do limite utilizado
                      {maxStudents > 0 && (
                        <>
                          {" "}
                          ·{" "}
                          <strong>
                            {formatNumber(
                              Math.max(0, maxStudents - activeStudents)
                            )}
                          </strong>{" "}
                          vaga(s) restante(s)
                        </>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </fieldset>

            <fieldset>
              <legend>Planos disponíveis</legend>

              <div className="mantenedora-plans-table-wrapper">
                <table className="mantenedora-plans-table">
                  <thead>
                    <tr>
                      <th>Número de alunos</th>
                      <th>Onboarding</th>
                      <th>Valor onboarding</th>
                      <th>Valor mensal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plans.map((plan) => {
                      const isCurrent = plan.id === data?.planId;
                      const isSelected = plan.id === selectedPlanId;
                      return (
                        <tr
                          key={plan.id}
                          className={isSelected ? "is-selected" : ""}
                        >
                          <td>
                            {formatPlanStudents(plan)}
                            {isCurrent && (
                              <Badge text="Plano atual" color="blue" />
                            )}
                          </td>
                          <td>{formatOnboarding(plan.onboardingHours)}</td>
                          <td>{formatBRL(plan.onboardingValue)}</td>
                          <td>{formatBRL(plan.monthlyValue)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </fieldset>

            <div className="form-actions">
              <Button
                text="Salvar Alterações"
                variation="primary"
                size="big"
                icon={CheckedCircle}
                loading={loading}
              />
            </div>
          </form>
        </div>
      </div>

      <PlanChangeModal
        step={
          showConfirmModal ? "confirm" : showSuccessModal ? "success" : null
        }
        fromPlan={fromPlan}
        toPlan={toPlan}
        loading={loading}
        onConfirm={handleConfirmPlanChange}
        onCancel={handleCloseConfirm}
        onCloseSuccess={handleCloseSuccess}
      />
    </div>
  );
};

export default Mantenedora;
